import { getCachedSpotsInArea, fetchAllRoutes } from './firebaseService';
import { MapBounds } from '../types';
import { saveSpotsToIDB } from './spotCacheService';

export interface OfflineDataDownloadResult {
  spotsCount: number;
  routesCount: number;
}

const GPX_CACHE_NAME = 'fricamp-gpx-v1';

/**
 * Pre-henter steder og ruter for et gitt kartområde.
 * Med Firestore offline persistence aktivert, caches queriene automatisk i IndexedDB.
 * GPX-filer lagres i Cache API for offline-tilgang.
 */
export async function downloadDataForArea(
  bounds: MapBounds,
  options: { includeSpots: boolean; includeRoutes: boolean },
  onProgress?: (message: string) => void
): Promise<OfflineDataDownloadResult> {
  let spotsCount = 0;
  let routesCount = 0;

  if (options.includeSpots) {
    onProgress?.('Henter steder...');
    const spots = await getCachedSpotsInArea(bounds);
    spotsCount = spots.length;
    if (spots.length > 0) {
      await saveSpotsToIDB(spots);
    }
  }

  if (options.includeRoutes) {
    onProgress?.('Henter ruter...');
    const allRoutes = await fetchAllRoutes();
    const routesInArea = allRoutes.filter(r => {
      if (r.center) {
        return r.center.latitude >= bounds.latMin &&
               r.center.latitude <= bounds.latMax &&
               r.center.longitude >= bounds.lonMin &&
               r.center.longitude <= bounds.lonMax;
      }
      return false;
    });
    routesCount = routesInArea.length;

    // Pre-hent GPX-filer for ruter i området
    if (routesInArea.length > 0) {
      onProgress?.('Henter GPX-filer...');
      const gpxCache = await caches.open(GPX_CACHE_NAME);
      for (const route of routesInArea) {
        if (route.gpxUrl) {
          try {
            const existing = await gpxCache.match(route.gpxUrl);
            if (!existing) {
              const response = await fetch(route.gpxUrl);
              if (response.ok) {
                await gpxCache.put(route.gpxUrl, response);
              }
            }
          } catch {
            // GPX-nedlasting feilet — ikke kritisk
          }
        }
      }
    }
  }

  return { spotsCount, routesCount };
}
